import { Link } from 'react-router-dom';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import Checkbox from 'components/shared/form/Checkbox';
import { simpleDate } from 'lib/date';
import type { RetroactiveDischargeRunItem } from 'services/api/admin/pccRetroactiveDischargeRuns';
import { colors } from 'styles/theme/colors';

import { dispositionLabel, isApprovable, liveOutcomeLabel, previewOutcomeLabel } from './labels';

export type ReviewRow = {
  item: RetroactiveDischargeRunItem;
  // The same stay on the newest live run started from this preview, once one has taken it
  live?: RetroactiveDischargeRunItem;
  // The live run holding this stay failed or stalled before it was sent; re-running that run finishes it
  liveStopped?: boolean;
};

type Props = {
  rows: ReviewRow[];
  selectable: boolean;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: (checked: boolean) => void;
  onSendOne: (item: RetroactiveDischargeRunItem) => void;
  busy: boolean;
};

// A row is open for approval until a live run takes it; a stay that got no answer can go again.
export function isSelectableRow(row: ReviewRow) {
  return isApprovable(row.item) && (!row.live || row.live.outcome === 'no_response');
}

export default function ReviewTable({ rows, selectable, selectedIds, onToggle, onToggleAll, onSendOne, busy }: Props) {
  const selectableRows = rows.filter(isSelectableRow);
  const allSelected = selectableRows.length > 0 && selectableRows.every((row) => selectedIds.has(row.item.id));
  const someSelected = selectableRows.some((row) => selectedIds.has(row.item.id));

  return (
    <Wrap>
      <Table aria-label='Patients PointClickCare discharged'>
        <thead>
          <tr>
            {selectable && (
              <Th>
                <Checkbox
                  size={20}
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  disabled={!selectableRows.length || busy}
                  onChange={() => onToggleAll(!allSelected)}
                  dataTestId='select-all-patients'
                />
              </Th>
            )}
            <Th>Patient</Th>
            <Th>Admitted</Th>
            <Th>PCC discharge</Th>
            <Th>PCC disposition</Th>
            <Th>{selectable ? 'Olio will' : 'Outcome'}</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <Row key={row.item.id} row={row} {...{ selectable, selectedIds, onToggle, onSendOne, busy }} />
          ))}
          {!rows.length && (
            <tr>
              <Td colSpan={selectable ? 7 : 6}>
                <Muted>No In Treatment PointClickCare stays were found for this client.</Muted>
              </Td>
            </tr>
          )}
        </tbody>
      </Table>
    </Wrap>
  );
}

type RowProps = Omit<Props, 'rows' | 'onToggleAll'> & { row: ReviewRow };

function Row({ row, selectable, selectedIds, onToggle, onSendOne, busy }: RowProps) {
  const { item, live } = row;
  const canSelect = isSelectableRow(row);
  const greyed = !isApprovable(item) && !live;
  const patientLink = `/patients/${item.episodeId}?locationEpisodeId=${item.locationEpisodeId}`;

  return (
    <Tr $muted={greyed} data-testid={`review-row-${item.id}`}>
      {selectable && (
        <Td>
          {canSelect && (
            <Checkbox
              size={20}
              checked={selectedIds.has(item.id)}
              disabled={busy}
              onChange={() => onToggle(item.id)}
              dataTestId={`select-${item.id}`}
            />
          )}
        </Td>
      )}
      <Td>
        <PatientName>
          <Link to={patientLink}>{item.patient.name}</Link>
        </PatientName>
        <Sub>{item.facility.name}</Sub>
      </Td>
      <Td>{simpleDate(item.admittedAt) ?? '—'}</Td>
      <Td>{simpleDate(item.dischargeDate) ?? '—'}</Td>
      <Td>{dispositionLabel(item)}</Td>
      <Td>{live ? liveOutcomeLabel(live, row.liveStopped) : previewOutcomeLabel(item)}</Td>
      <Td>
        {live?.outcome === 'discharged' ? (
          <Link to={patientLink}>Open patient</Link>
        ) : canSelect && selectable && !item.leftForStaff ? (
          <Button variant='ghost' onClick={() => onSendOne(item)} disabled={busy} data-testid={`send-one-${item.id}`}>
            Send one
          </Button>
        ) : null}
      </Td>
    </Tr>
  );
}

const Wrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  font-weight: 500;
  padding: 8px;
  border-bottom: 1px solid ${colors.black10};
  font-size: 12px;
  color: ${colors.black75};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 8px;
  border-bottom: 1px solid ${colors.black5};
  vertical-align: middle;
  white-space: nowrap;
`;

const Tr = styled.tr<{ $muted: boolean }>`
  color: ${({ $muted }) => ($muted ? colors.black50 : colors.black100)};
`;

const PatientName = styled.div`
  font-weight: 500;
`;

const Sub = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${colors.black75};
`;

const Muted = styled.span`
  color: ${colors.black50};
`;
